import { useState } from 'react';
import axios from 'axios';
import { Lock, Loader2, ArrowLeft } from 'lucide-react';

/**
 * The admin console's second factor.
 *
 * Session auth already proved who you are; this proves you meant to open a
 * page that lists every customer's email, plan and revenue. Shown whenever
 * GET /admin/session reports the console as locked, and re-shown whenever any
 * admin call comes back 401.
 */
export default function AdminUnlock({
  configured,
  onUnlocked,
  onBack,
}: {
  configured: boolean;
  onUnlocked: () => void;
  onBack: () => void;
}) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      await axios.post('/api/admin/unlock', { password });
      setPassword('');
      onUnlocked();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not unlock the console.');
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-[#6B6670] hover:text-[#2D2A2E] mb-6 font-sans transition-colors"
        >
          <ArrowLeft size={16} /> Back to Dashboard
        </button>

        <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-7 shadow-sm">
          <div className="w-11 h-11 rounded-full bg-[#4A2D5E]/10 flex items-center justify-center mb-5">
            <Lock size={19} className="text-[#4A2D5E]" />
          </div>

          <h1 className="font-serif text-2xl text-[#2D2A2E] mb-1.5">Admin console</h1>
          <p className="font-sans text-sm text-[#6B6670] leading-relaxed mb-6">
            This page shows every customer's data. Enter the admin password to
            unlock it for the next 8 hours.
          </p>

          {!configured ? (
            /* Fail-closed is the correct behaviour, but it must SAY so — a
               blank refusal here would read as a bug and send you hunting. */
            <div className="bg-[#B54A4A]/5 border border-[#B54A4A]/20 rounded-[10px] p-4">
              <p className="font-sans text-sm text-[#B54A4A] leading-relaxed">
                <span className="font-medium">Not configured.</span> The console is
                locked because <code className="font-mono text-xs">ADMIN_PASSWORD_HASH</code>{' '}
                is not set in the environment. Set it in Render, then redeploy.
              </p>
            </div>
          ) : (
            <form onSubmit={submit}>
              <label
                htmlFor="admin-password"
                className="block font-sans text-xs text-[#6B6670] uppercase tracking-wider mb-2"
              >
                Admin password
              </label>
              <input
                id="admin-password"
                type="password"
                autoFocus
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                className="w-full px-3.5 py-2.5 rounded-[10px] border border-[#E6E2DE] bg-[#FAF9F7]
                           font-sans text-sm text-[#2D2A2E] outline-none transition-colors
                           focus:border-[#4A2D5E] focus:bg-white disabled:opacity-60"
              />

              {error && (
                <p role="alert" className="font-sans text-sm text-[#B54A4A] mt-3">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={busy || !password}
                className="w-full mt-5 px-4 py-2.5 rounded-[10px] bg-[#4A2D5E] text-white
                           font-sans text-sm font-medium transition-opacity
                           hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed
                           flex items-center justify-center gap-2"
              >
                {busy && <Loader2 size={15} className="animate-spin" />}
                {busy ? 'Unlocking…' : 'Unlock console'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
